import { Button, Card, Elevation, FormGroup, Icon, InputGroup, Slider, Callout } from '@blueprintjs/core'
import { toast } from 'botpress/shared'
import React, { FC, useState, useEffect } from 'react'
import api from '~/app/api'
import { subDataStore } from '../../../token-store'
import logo from '../../assets/images/xmati.png'


interface Props {
  botId: string
}


interface ChatInterfaceConfig {
  window?: {
    width?: number
    height?: number
    borderRadius?: number
    backgroundColor?: string
    enableGradient?: boolean
    gradientStart?: string
    gradientEnd?: string
    gradientDirection?: 'to-bottom' | 'to-top' | 'to-right' | 'to-left' | 'diagonal'
  }
  header?: {
    backgroundColor?: string
    textColor?: string
    botTitle?: string
  }
  fallback?: {
    message?: string
  }
  branding?: {
    enabled?: boolean
  }
  messageBox?: {
    bot?: {
      borderRadius?: number
      borderColor?: string
      backgroundColor?: string
      textColor?: string
    }
    user?: {
      borderRadius?: number
      borderColor?: string
      backgroundColor?: string
      textColor?: string
    }
  }
  composer?: {
    showIcon?: boolean
    textarea?: {
      backgroundColor?: string
      borderColor?: string
      borderRadius?: number
      textColor?: string
      width?: number
    }
    sendButton?: {
      backgroundColor?: string
      textColor?: string
      borderRadius?: number
      width?: number
    }
  }
  welcome?: {
    message?: string
  }
}


const ChatInterfaceCustomization: FC<Props> = ({ botId }) => {
  // Window settings
  const [windowWidth, setWindowWidth] = useState(360) // Pixels: 300-430px
  const [windowHeight, setWindowHeight] = useState(85) // Percentage: 70-100%
  const [windowBorderRadius, setWindowBorderRadius] = useState(8)
  const [windowBgColor, setWindowBgColor] = useState('#FFFFFF')
  const [enableGradient, setEnableGradient] = useState(false)
  const [gradientStart, setGradientStart] = useState('#FFFFFF')
  const [gradientEnd, setGradientEnd] = useState('#F0F0F0')
  const [gradientDirection, setGradientDirection] = useState<'to-bottom' | 'to-top' | 'to-right' | 'to-left' | 'diagonal'>('to-bottom')


  // Header settings
  const [headerBgColor, setHeaderBgColor] = useState('#FFFFFF')
  const [headerTextColor, setHeaderTextColor] = useState('#000000')
  const [botTitleName, setBotTitleName] = useState('Chatbot')


  // Welcome message
  const [initialWelcomeMessage, setInitialWelcomeMessage] = useState('Hello! How can I help you today?')
  const [fallbackMessage, setFallbackMessage] = useState("Sorry, I didn't understand that. Let me help you with what I can do.")


  // Branding settings
  const [showBranding, setShowBranding] = useState(true)


  // Bot message box settings
  const [botBorderRadius, setBotBorderRadius] = useState(8)
  const [botBorderColor, setBotBorderColor] = useState('#F1F1F1')
  const [botBgColor, setBotBgColor] = useState('#FFFFFF')
  const [botTextColor, setBotTextColor] = useState('#000000')


  // User message box settings
  const [userBorderRadius, setUserBorderRadius] = useState(8)
  const [userBorderColor, setUserBorderColor] = useState('#F1F1F1')
  const [userBgColor, setUserBgColor] = useState('#000000')
  const [userTextColor, setUserTextColor] = useState('#FFFFFF')


  // Composer icon settings
  const [showComposerIcon, setShowComposerIcon] = useState(true)


  // Textarea settings
  const [textareaBgColor, setTextareaBgColor] = useState('#F3F3F3')
  const [textareaBorderColor, setTextareaBorderColor] = useState('#E0E0E0')
  const [textareaBorderRadius, setTextareaBorderRadius] = useState(20)
  const [textareaTextColor, setTextareaTextColor] = useState('#333333')
  const [textareaWidth, setTextareaWidth] = useState(70) // Percentage: 50-80%


  // Send button settings
  const [sendButtonBgColor, setSendButtonBgColor] = useState('#FFFFFF')
  const [sendButtonTextColor, setSendButtonTextColor] = useState('#666666')
  const [sendButtonBorderRadius, setSendButtonBorderRadius] = useState(5)
  const [sendButtonWidth, setSendButtonWidth] = useState(70) // Pixels: 50-80px


  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [botName, setBotName] = useState('Bot')


  // Get subscription data
  const savedSubData = subDataStore.get() || {}
  const subscription = savedSubData.subscription || ''
  // Check if user has access to advanced features
  // Starter plan: only window settings, text input, send button
  // Trial, Demo, Pro: all features
  const hasAdvancedFeatures = subscription.toLowerCase() !== 'starter'


  useEffect(() => {
    void loadConfig()
  }, [botId])


  const loadConfig = async () => {
    try {
      setIsLoading(true)


      const { data } = await api.getSecured().get(`/admin/workspace/bots/${botId}/module-config/channel-web`)


      const config = data?.payload || data


      // Load bot name from config
      if (config?.botName) {
        setBotName(config.botName)
      }


      if (config?.chatInterface) {
        // Window settings
        if (config.chatInterface.window) {
          setWindowWidth(config.chatInterface.window.width || 360)
          setWindowHeight(config.chatInterface.window.height || 80)
          setWindowBorderRadius(config.chatInterface.window.borderRadius || 8)
          setWindowBgColor(config.chatInterface.window.backgroundColor || '#FFFFFF')
          setEnableGradient(config.chatInterface.window.enableGradient || false)
          setGradientStart(config.chatInterface.window.gradientStart || '#FFFFFF')
          setGradientEnd(config.chatInterface.window.gradientEnd || '#F0F0F0')
          setGradientDirection(config.chatInterface.window.gradientDirection || 'to-bottom')
        }


        // Header settings
        if (config.chatInterface.header) {
          setHeaderBgColor(config.chatInterface.header.backgroundColor || '#FFFFFF')
          setHeaderTextColor(config.chatInterface.header.textColor || '#000000')
          setBotTitleName(config.chatInterface.header.botTitle || 'Chatbot')
        }


        // Welcome message
        if (config.chatInterface.welcome) {
          setInitialWelcomeMessage(config.chatInterface.welcome.message || 'Hello! How can I help you today?')
        }


        if (config.chatInterface.fallback) {
          setFallbackMessage(
            config.chatInterface.fallback.message || "Sorry, I didn't understand that. Let me help you with what I can do."
          )
        }


        // Branding settings
        if (config.chatInterface.branding !== undefined) {
          setShowBranding(config.chatInterface.branding.enabled !== false)
        }


        // Bot message box settings
        if (config.chatInterface.messageBox?.bot) {
          setBotBorderRadius(config.chatInterface.messageBox.bot.borderRadius || 8)
          setBotBorderColor(config.chatInterface.messageBox.bot.borderColor || '#F1F1F1')
          setBotBgColor(config.chatInterface.messageBox.bot.backgroundColor || '#FFFFFF')
          setBotTextColor(config.chatInterface.messageBox.bot.textColor || '#000000')
        }


        // User message box settings
        if (config.chatInterface.messageBox?.user) {
          setUserBorderRadius(config.chatInterface.messageBox.user.borderRadius || 8)
          setUserBorderColor(config.chatInterface.messageBox.user.borderColor || '#F1F1F1')
          setUserBgColor(config.chatInterface.messageBox.user.backgroundColor || '#000000')
          setUserTextColor(config.chatInterface.messageBox.user.textColor || '#FFFFFF')
        }


        // Composer settings
        if (config.chatInterface.composer) {
          setShowComposerIcon(config.chatInterface.composer.showIcon !== false)


          // Textarea settings
          if (config.chatInterface.composer.textarea) {
            setTextareaBgColor(config.chatInterface.composer.textarea.backgroundColor || '#F3F3F3')
            setTextareaBorderColor(config.chatInterface.composer.textarea.borderColor || '#E0E0E0')
            setTextareaBorderRadius(config.chatInterface.composer.textarea.borderRadius || 20)
            setTextareaTextColor(config.chatInterface.composer.textarea.textColor || '#333333')
            setTextareaWidth(config.chatInterface.composer.textarea.width || 100)
          }


          // Send button settings
          if (config.chatInterface.composer.sendButton) {
            setSendButtonBgColor(config.chatInterface.composer.sendButton.backgroundColor || '#FFFFFF')
            setSendButtonTextColor(config.chatInterface.composer.sendButton.textColor || '#666666')
            setSendButtonBorderRadius(config.chatInterface.composer.sendButton.borderRadius || 5)
            setSendButtonWidth(config.chatInterface.composer.sendButton.width || 70)
          }
        }
      }
    } catch (error: any) {
      console.error('Failed to load chat interface config:', error)
    } finally {
      setIsLoading(false)
    }
  }


  const saveConfig = async () => {
    try {
      setIsSaving(true)


      // First, get the existing config
      const { data } = await api.getSecured().get(`/admin/workspace/bots/${botId}/module-config/channel-web`)
      const existingConfig = data?.payload || data || {}


      const chatInterfaceConfig: ChatInterfaceConfig = {
        window: {
          width: windowWidth,
          height: windowHeight,
          borderRadius: windowBorderRadius,
          backgroundColor: windowBgColor,
          // Gradient is available for all users
          enableGradient,
          gradientStart,
          gradientEnd,
          gradientDirection
        },
        header: {
          backgroundColor: headerBgColor,
          textColor: headerTextColor,
          botTitle: botTitleName
        },
        branding: hasAdvancedFeatures ? {
          enabled: showBranding
        } : {
          enabled: true
        },
        messageBox: hasAdvancedFeatures ? {
          bot: {
            borderRadius: botBorderRadius,
            borderColor: botBorderColor,
            backgroundColor: botBgColor,
            textColor: botTextColor
          },
          user: {
            borderRadius: userBorderRadius,
            borderColor: userBorderColor,
            backgroundColor: userBgColor,
            textColor: userTextColor
          }
        } : {
          bot: {
            borderRadius: 8,
            borderColor: '#F1F1F1',
            backgroundColor: '#FFFFFF',
            textColor: '#000000'
          },
          user: {
            borderRadius: 8,
            borderColor: '#F1F1F1',
            backgroundColor: '#000000',
            textColor: '#FFFFFF'
          }
        },
        composer: {
          showIcon: hasAdvancedFeatures ? showComposerIcon : true,
          textarea: {
            backgroundColor: textareaBgColor,
            borderColor: textareaBorderColor,
            borderRadius: textareaBorderRadius,
            textColor: textareaTextColor,
            width: textareaWidth
          },
          sendButton: {
            backgroundColor: sendButtonBgColor,
            textColor: sendButtonTextColor,
            borderRadius: sendButtonBorderRadius,
            width: sendButtonWidth
          }
        },
        welcome: {
          message: initialWelcomeMessage
        },
        fallback: {
          message: fallbackMessage
        }
      }


      // Send only the chatInterface field to be merged with existing config
      const payload = {
        chatInterface: chatInterfaceConfig
      }


      await api.getSecured().post(`/admin/workspace/bots/${botId}/module-config/channel-web`, payload)


      toast.success('Chat interface customization saved successfully!')
    } catch (error: any) {
      console.error('Failed to save chat interface config:', error)
      toast.failure(`Failed to save configuration: ${error.message || 'Unknown error'}`)
    } finally {
      setIsSaving(false)
    }
  }


  const resetToDefaults = () => {
    void loadConfig()
  }


  const getGradientStyle = () => {
    if (!enableGradient) {
      return windowBgColor
    }


    const directions = {
      'to-bottom': '180deg',
      'to-top': '0deg',
      'to-right': '90deg',
      'to-left': '270deg',
      'diagonal': '135deg'
    }


    return `linear-gradient(${directions[gradientDirection]}, ${gradientStart}, ${gradientEnd})`
  }


  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left Column - Scrollable Settings */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 42px', borderRight: '1px solid #e1e4e8' }}>
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
            Chat Interface Customization
          </h4>
          <p style={{ margin: 0, fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
            Customize the appearance of your chat window and message bubbles.
          </p>
        </div>


        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <Icon icon="refresh" iconSize={24} />
            <p style={{ marginTop: '12px' }}>Loading configuration...</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gap: '24px' }}>
              {/* Upgrade Notice for Starter Plan */}
              {!hasAdvancedFeatures && (
                <Callout
                  intent="warning"
                  icon="lock"
                  title="Upgrade to Access Advanced Features"
                  style={{ marginBottom: '8px' }}
                >
                  <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.5' }}>
                    You're currently on the <strong>Starter</strong> plan. Upgrade to <strong>Professional</strong> to unlock:
                  </p>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '13px' }}>
                    <li>Bot & User Message Box Styling</li>
                    <li>Composer Icon Settings</li>
                    <li>xMati Branding Removal</li>
                  </ul>
                </Callout>
              )}


              {/* WINDOW SETTINGS */}
              <Card elevation={Elevation.ONE} style={{ padding: '20px', backgroundColor: '#f8f9fa' }}>
                <h5 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="application" iconSize={16} color="#5C7CFA" />
                  Chat Window Settings
                </h5>


                {/* Window Width */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Width: {windowWidth}px</span>}
                  helperText="Width of the chat window"
                >
                  <Slider
                    min={300}
                    max={430}
                    stepSize={10}
                    labelStepSize={50}
                    value={windowWidth}
                    onChange={setWindowWidth}
                    showTrackFill
                  />
                </FormGroup>


                {/* Window Height */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Height: {windowHeight}%</span>}
                  helperText="Height of the chat window (percentage of viewport)"
                >
                  <Slider
                    min={70}
                    max={100}
                    stepSize={5}
                    labelStepSize={10}
                    value={windowHeight}
                    onChange={setWindowHeight}
                    showTrackFill
                  />
                </FormGroup>


                {/* Window Border Radius */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Border Radius: {windowBorderRadius}px</span>}
                  helperText="Roundness of the window corners"
                >
                  <Slider
                    min={0}
                    max={24}
                    stepSize={2}
                    labelStepSize={6}
                    value={windowBorderRadius}
                    onChange={setWindowBorderRadius}
                    showTrackFill
                  />
                </FormGroup>


                {/* Enable Gradient */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Background Style</span>}
                >
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
                    <input
                      type="checkbox"
                      checked={enableGradient}
                      onChange={(e) => setEnableGradient(e.target.checked)}
                      style={{ marginRight: '8px', cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '13px', color: '#333' }}>Use gradient background</span>
                  </label>
                </FormGroup>


                {/* Body Background Color (Solid) */}
                {!enableGradient && (
                  <FormGroup
                    label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Body Background Color</span>}
                    helperText="Background color for the message area"
                  >
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="color"
                        value={windowBgColor}
                        onChange={(e) => setWindowBgColor(e.target.value)}
                        style={{ width: '50px', height: '36px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px' }}
                      />
                      <InputGroup
                        value={windowBgColor}
                        onChange={(e) => setWindowBgColor(e.target.value)}
                        placeholder="#FFFFFF"
                      />
                    </div>
                  </FormGroup>
                )}


                {/* Header Background Color - Available for all users */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Header Background Color</span>}
                  helperText="Background color for the chat header"
                >
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={headerBgColor}
                      onChange={(e) => setHeaderBgColor(e.target.value)}
                      style={{ width: '50px', height: '36px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <InputGroup
                      value={headerBgColor}
                      onChange={(e) => setHeaderBgColor(e.target.value)}
                      placeholder="#FFFFFF"
                    />
                  </div>
                </FormGroup>


                {/* Header Text Color */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Header Text & Icon Color</span>}
                  helperText="Color for the header text and icons"
                >
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={headerTextColor}
                      onChange={(e) => setHeaderTextColor(e.target.value)}
                      style={{ width: '50px', height: '36px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <InputGroup
                      value={headerTextColor}
                      onChange={(e) => setHeaderTextColor(e.target.value)}
                      placeholder="#000000"
                    />
                  </div>
                </FormGroup>


                {/* Bot Title Name */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Bot Title Name</span>}
                  helperText="Name displayed in the chat header"
                >
                  <InputGroup
                    value={botTitleName}
                    onChange={(e) => setBotTitleName(e.target.value)}
                    placeholder="Chatbot"
                    maxLength={50}
                  />
                </FormGroup>


                {/* Gradient Settings */}
                {enableGradient && (
                  <>
                    <FormGroup
                      label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Gradient Start Color</span>}
                    >
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="color"
                          value={gradientStart}
                          onChange={(e) => setGradientStart(e.target.value)}
                          style={{ width: '50px', height: '36px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px' }}
                        />
                        <InputGroup
                          value={gradientStart}
                          onChange={(e) => setGradientStart(e.target.value)}
                          placeholder="#FFFFFF"
                        />
                      </div>
                    </FormGroup>


                    <FormGroup
                      label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Gradient End Color</span>}
                    >
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="color"
                          value={gradientEnd}
                          onChange={(e) => setGradientEnd(e.target.value)}
                          style={{ width: '50px', height: '36px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px' }}
                        />
                        <InputGroup
                          value={gradientEnd}
                          onChange={(e) => setGradientEnd(e.target.value)}
                          placeholder="#F0F0F0"
                        />
                      </div>
                    </FormGroup>


                    <FormGroup
                      label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Gradient Direction</span>}
                    >
                      <div className="bp3-select">
                        <select
                          value={gradientDirection}
                          onChange={(e) => setGradientDirection(e.target.value as any)}
                          style={{ width: '100%', padding: '8px', fontSize: '13px' }}
                        >
                          <option value="to-bottom">Top to Bottom</option>
                          <option value="to-top">Bottom to Top</option>
                          <option value="to-right">Left to Right</option>
                          <option value="to-left">Right to Left</option>
                          <option value="diagonal">Diagonal</option>
                        </select>
                      </div>
                    </FormGroup>
                  </>
                )}
              </Card>


              {/* INITIAL WELCOME MESSAGE SETTINGS */}
              <Card elevation={Elevation.ONE} style={{ padding: '20px', backgroundColor: '#f8f9fa' }}>
                <h5 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="chat" iconSize={16} color="#5C7CFA" />
                  Welcome Message
                </h5>


                {/* Initial Welcome Message */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Initial Welcome Message</span>}
                  helperText="Message displayed when the chat opens for the first time"
                >
                  <textarea
                    value={initialWelcomeMessage}
                    onChange={(e) => setInitialWelcomeMessage(e.target.value)}
                    placeholder="Hello! How can I help you today?"
                    maxLength={500}
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      padding: '12px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      color: '#333'
                    }}
                  />
                  <div style={{ marginTop: '6px', fontSize: '12px', color: '#999' }}>
                    {initialWelcomeMessage.length}/500 characters
                  </div>
                </FormGroup>
              </Card>


              <Card elevation={Elevation.ONE} style={{ padding: '20px', backgroundColor: '#f8f9fa' }}>
                <h5 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="help" iconSize={16} color="#5C7CFA" />
                  Fallback Message
                </h5>


                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Fallback Text</span>}
                  helperText="Message sent when the bot cannot understand the user's input."
                >
                  <textarea
                    value={fallbackMessage}
                    onChange={e => setFallbackMessage(e.target.value)}
                    placeholder="Sorry, I didn't understand that. Let me help you with what I can do."
                    maxLength={500}
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      padding: '12px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      color: '#333'
                    }}
                  />
                  <div style={{ marginTop: '6px', fontSize: '12px', color: '#999' }}>
                    {fallbackMessage.length}/500 characters
                  </div>
                </FormGroup>
              </Card>


              {/* BOT MESSAGE BOX SETTINGS - Locked for Starter */}
              <Card elevation={Elevation.ONE} style={{ padding: '20px', backgroundColor: '#f8f9fa', opacity: hasAdvancedFeatures ? 1 : 0.6, position: 'relative' }}>
                {!hasAdvancedFeatures && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '3px',
                    zIndex: 10,
                    cursor: 'not-allowed'
                  }}>
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Icon icon="lock" iconSize={32} color="#999" style={{ marginBottom: '8px' }} />
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#666' }}>
                        Upgrade to Professional
                      </div>
                    </div>
                  </div>
                )}
                <h5 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="chat" iconSize={16} color="#5C7CFA" />
                  Bot Message Box
                </h5>


                {/* Bot Border Radius */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Border Radius: {botBorderRadius}px</span>}
                >
                  <Slider
                    min={0}
                    max={24}
                    stepSize={2}
                    labelStepSize={6}
                    value={botBorderRadius}
                    onChange={hasAdvancedFeatures ? setBotBorderRadius : () => { }}
                    disabled={!hasAdvancedFeatures}
                    showTrackFill
                  />
                </FormGroup>


                {/* Bot Border Color */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Border Color</span>}
                >
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={botBorderColor}
                      onChange={(e) => hasAdvancedFeatures && setBotBorderColor(e.target.value)}
                      disabled={!hasAdvancedFeatures}
                      style={{ width: '50px', height: '36px', cursor: hasAdvancedFeatures ? 'pointer' : 'not-allowed', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <InputGroup
                      value={botBorderColor}
                      onChange={(e) => hasAdvancedFeatures && setBotBorderColor(e.target.value)}
                      disabled={!hasAdvancedFeatures}
                      placeholder="#F1F1F1"
                    />
                  </div>
                </FormGroup>


                {/* Bot Background Color */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Background Color</span>}
                >
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={botBgColor}
                      onChange={(e) => hasAdvancedFeatures && setBotBgColor(e.target.value)}
                      disabled={!hasAdvancedFeatures}
                      style={{ width: '50px', height: '36px', cursor: hasAdvancedFeatures ? 'pointer' : 'not-allowed', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <InputGroup
                      value={botBgColor}
                      onChange={(e) => hasAdvancedFeatures && setBotBgColor(e.target.value)}
                      disabled={!hasAdvancedFeatures}
                      placeholder="#FFFFFF"
                    />
                  </div>
                </FormGroup>


                {/* Bot Text Color */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Text Color</span>}
                >
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={botTextColor}
                      onChange={(e) => hasAdvancedFeatures && setBotTextColor(e.target.value)}
                      disabled={!hasAdvancedFeatures}
                      style={{ width: '50px', height: '36px', cursor: hasAdvancedFeatures ? 'pointer' : 'not-allowed', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <InputGroup
                      value={botTextColor}
                      onChange={(e) => hasAdvancedFeatures && setBotTextColor(e.target.value)}
                      disabled={!hasAdvancedFeatures}
                      placeholder="#000000"
                    />
                  </div>
                </FormGroup>
              </Card>


              {/* USER MESSAGE BOX SETTINGS - Locked for Starter */}
              <Card elevation={Elevation.ONE} style={{ padding: '20px', backgroundColor: '#f8f9fa', opacity: hasAdvancedFeatures ? 1 : 0.6, position: 'relative' }}>
                {!hasAdvancedFeatures && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '3px',
                    zIndex: 10,
                    cursor: 'not-allowed'
                  }}>
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Icon icon="lock" iconSize={32} color="#999" style={{ marginBottom: '8px' }} />
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#666' }}>
                        Upgrade to Professional
                      </div>
                    </div>
                  </div>
                )}
                <h5 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="user" iconSize={16} color="#5C7CFA" />
                  User Message Box
                </h5>


                {/* User Border Radius */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Border Radius: {userBorderRadius}px</span>}
                >
                  <Slider
                    min={0}
                    max={24}
                    stepSize={2}
                    labelStepSize={6}
                    value={userBorderRadius}
                    onChange={hasAdvancedFeatures ? setUserBorderRadius : () => { }}
                    disabled={!hasAdvancedFeatures}
                    showTrackFill
                  />
                </FormGroup>


                {/* User Border Color */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Border Color</span>}
                >
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={userBorderColor}
                      onChange={(e) => hasAdvancedFeatures && setUserBorderColor(e.target.value)}
                      disabled={!hasAdvancedFeatures}
                      style={{ width: '50px', height: '36px', cursor: hasAdvancedFeatures ? 'pointer' : 'not-allowed', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <InputGroup
                      value={userBorderColor}
                      onChange={(e) => hasAdvancedFeatures && setUserBorderColor(e.target.value)}
                      disabled={!hasAdvancedFeatures}
                      placeholder="#F1F1F1"
                    />
                  </div>
                </FormGroup>


                {/* User Background Color */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Background Color</span>}
                >
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={userBgColor}
                      onChange={(e) => hasAdvancedFeatures && setUserBgColor(e.target.value)}
                      disabled={!hasAdvancedFeatures}
                      style={{ width: '50px', height: '36px', cursor: hasAdvancedFeatures ? 'pointer' : 'not-allowed', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <InputGroup
                      value={userBgColor}
                      onChange={(e) => hasAdvancedFeatures && setUserBgColor(e.target.value)}
                      disabled={!hasAdvancedFeatures}
                      placeholder="#000000"
                    />
                  </div>
                </FormGroup>


                {/* User Text Color */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Text Color</span>}
                >
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={userTextColor}
                      onChange={(e) => hasAdvancedFeatures && setUserTextColor(e.target.value)}
                      disabled={!hasAdvancedFeatures}
                      style={{ width: '50px', height: '36px', cursor: hasAdvancedFeatures ? 'pointer' : 'not-allowed', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <InputGroup
                      value={userTextColor}
                      onChange={(e) => hasAdvancedFeatures && setUserTextColor(e.target.value)}
                      disabled={!hasAdvancedFeatures}
                      placeholder="#FFFFFF"
                    />
                  </div>
                </FormGroup>
              </Card>


              {/* COMPOSER ICON SETTINGS - Locked for Starter */}
              <Card elevation={Elevation.ONE} style={{ padding: '20px', backgroundColor: '#f8f9fa', opacity: hasAdvancedFeatures ? 1 : 0.6, position: 'relative' }}>
                {!hasAdvancedFeatures && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '3px',
                    zIndex: 10,
                    cursor: 'not-allowed'
                  }}>
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Icon icon="lock" iconSize={32} color="#999" style={{ marginBottom: '8px' }} />
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#666' }}>
                        Upgrade to Professional
                      </div>
                    </div>
                  </div>
                )}
                <h5 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="media" iconSize={16} color="#5C7CFA" />
                  Composer Icon
                </h5>


                {/* Show/Hide Composer Icon */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Display Icon</span>}
                  helperText="Show the default xMati icon before the text input field"
                >
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: hasAdvancedFeatures ? 'pointer' : 'not-allowed' }}>
                      <input
                        type="checkbox"
                        checked={showComposerIcon}
                        onChange={(e) => hasAdvancedFeatures && setShowComposerIcon(e.target.checked)}
                        disabled={!hasAdvancedFeatures}
                        style={{ width: '16px', height: '16px', cursor: hasAdvancedFeatures ? 'pointer' : 'not-allowed' }}
                      />
                      <span style={{ fontSize: '13px', color: '#555' }}>Show icon before text input</span>
                    </label>
                  </div>
                </FormGroup>
              </Card>


              {/* BRANDING SETTINGS - Locked for Starter */}
              <Card elevation={Elevation.ONE} style={{ padding: '20px', backgroundColor: '#f8f9fa', opacity: hasAdvancedFeatures ? 1 : 0.6, position: 'relative' }}>
                {!hasAdvancedFeatures && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '3px',
                    zIndex: 10,
                    cursor: 'not-allowed'
                  }}>
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Icon icon="lock" iconSize={32} color="#999" style={{ marginBottom: '8px' }} />
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#666' }}>
                        Upgrade to Professional
                      </div>
                    </div>
                  </div>
                )}
                <h5 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="tag" iconSize={16} color="#5C7CFA" />
                  Branding
                </h5>


                {/* Show/Hide Branding */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Display Branding</span>}
                  helperText="Show 'We're by xMati' branding at the bottom of the chat"
                >
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: hasAdvancedFeatures ? 'pointer' : 'not-allowed' }}>
                      <input
                        type="checkbox"
                        checked={showBranding}
                        onChange={(e) => hasAdvancedFeatures && setShowBranding(e.target.checked)}
                        disabled={!hasAdvancedFeatures}
                        style={{ width: '16px', height: '16px', cursor: hasAdvancedFeatures ? 'pointer' : 'not-allowed' }}
                      />
                      <span style={{ fontSize: '13px', color: '#555' }}>Show "We're by xMati" at bottom</span>
                    </label>
                  </div>
                </FormGroup>
              </Card>
            </div>


            {/* Textarea Customization */}
            <div style={{ marginBottom: '20px' }}>
              <Card elevation={Elevation.ONE} style={{ padding: '20px' }}>
                <h5 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="edit" iconSize={16} color="#5C7CFA" />
                  Text Input Field
                </h5>


                {/* Textarea Background Color */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Background Color</span>}
                  helperText="Background color of the text input area"
                >
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={textareaBgColor}
                      onChange={(e) => setTextareaBgColor(e.target.value)}
                      style={{ width: '50px', height: '36px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <InputGroup
                      value={textareaBgColor}
                      onChange={(e) => setTextareaBgColor(e.target.value)}
                      placeholder="#F3F3F3"
                    />
                  </div>
                </FormGroup>


                {/* Textarea Border Color */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Border Color</span>}
                  helperText="Border color of the text input area"
                >
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={textareaBorderColor}
                      onChange={(e) => setTextareaBorderColor(e.target.value)}
                      style={{ width: '50px', height: '36px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <InputGroup
                      value={textareaBorderColor}
                      onChange={(e) => setTextareaBorderColor(e.target.value)}
                      placeholder="#E0E0E0"
                    />
                  </div>
                </FormGroup>


                {/* Textarea Border Radius */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Border Radius: {textareaBorderRadius}px</span>}
                  helperText="Roundness of the text input area corners"
                >
                  <Slider
                    min={0}
                    max={30}
                    stepSize={1}
                    labelStepSize={10}
                    value={textareaBorderRadius}
                    onChange={setTextareaBorderRadius}
                  />
                </FormGroup>


                {/* Textarea Text Color */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Text Color</span>}
                  helperText="Color of the text inside the input area"
                >
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={textareaTextColor}
                      onChange={(e) => setTextareaTextColor(e.target.value)}
                      style={{ width: '50px', height: '36px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <InputGroup
                      value={textareaTextColor}
                      onChange={(e) => setTextareaTextColor(e.target.value)}
                      placeholder="#333333"
                    />
                  </div>
                </FormGroup>


                {/* Textarea Width */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Width: {textareaWidth}%</span>}
                  helperText="Width of the text input area (percentage of available space)"
                >
                  <Slider
                    min={50}
                    max={90}
                    stepSize={1}
                    labelStepSize={10}
                    value={textareaWidth}
                    onChange={setTextareaWidth}
                  />
                </FormGroup>
              </Card>
            </div>


            {/* Send Button Customization */}
            <div style={{ marginBottom: '20px' }}>
              <Card elevation={Elevation.ONE} style={{ padding: '20px' }}>
                <h5 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="send-message" iconSize={16} color="#5C7CFA" />
                  Send Button
                </h5>


                {/* Send Button Background Color */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Background Color</span>}
                  helperText="Background color of the send button"
                >
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={sendButtonBgColor}
                      onChange={(e) => setSendButtonBgColor(e.target.value)}
                      style={{ width: '50px', height: '36px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <InputGroup
                      value={sendButtonBgColor}
                      onChange={(e) => setSendButtonBgColor(e.target.value)}
                      placeholder="#FFFFFF"
                    />
                  </div>
                </FormGroup>


                {/* Send Button Text Color */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Text Color</span>}
                  helperText="Color of the 'Send' text"
                >
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={sendButtonTextColor}
                      onChange={(e) => setSendButtonTextColor(e.target.value)}
                      style={{ width: '50px', height: '36px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <InputGroup
                      value={sendButtonTextColor}
                      onChange={(e) => setSendButtonTextColor(e.target.value)}
                      placeholder="#666666"
                    />
                  </div>
                </FormGroup>


                {/* Send Button Border Radius */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Border Radius: {sendButtonBorderRadius}px</span>}
                  helperText="Roundness of the send button corners"
                >
                  <Slider
                    min={0}
                    max={20}
                    stepSize={1}
                    labelStepSize={5}
                    value={sendButtonBorderRadius}
                    onChange={setSendButtonBorderRadius}
                  />
                </FormGroup>


                {/* Send Button Width */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Width: {sendButtonWidth}px</span>}
                  helperText="Width of the send button (pixels)"
                >
                  <Slider
                    min={50}
                    max={80}
                    stepSize={5}
                    labelStepSize={15}
                    value={sendButtonWidth}
                    onChange={setSendButtonWidth}
                  />
                </FormGroup>
              </Card>
            </div>


            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e1e4e8' }}>
              <Button
                text="Reset to Defaults"
                onClick={resetToDefaults}
                icon="refresh"
              />
              <Button
                text={isSaving ? 'Saving...' : 'Save Changes'}
                onClick={saveConfig}
                intent="primary"
                icon="floppy-disk"
                disabled={isSaving}
                loading={isSaving}
              />
            </div>


            {/* Information Card */}
            <Card
              elevation={Elevation.ONE}
              style={{
                marginTop: '20px',
                padding: '14px',
                backgroundColor: '#E8F4FD',
                borderLeft: '4px solid #2196F3'
              }}
            >
              <div style={{ display: 'flex', gap: '12px' }}>
                <Icon icon="info-sign" iconSize={16} color="#2196F3" style={{ marginTop: '2px', flexShrink: 0 }} />
                <div style={{ fontSize: '12px', color: '#333', lineHeight: '1.6' }}>
                  <strong>Note:</strong> After saving the configuration, the chat interface styling may take some time to reflect on your website. Users may need to refresh their browser to see the changes.
                </div>
              </div>
            </Card>
          </>
        )}
      </div>


      {/* Right Column - Fixed Preview */}
      <div style={{
        width: '500px',
        flexShrink: 0,
        backgroundColor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '10px 10px',
          borderBottom: '1px solid #e1e4e8',
          backgroundColor: '#fff'
        }}>
          <h5 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>
            Live Preview
          </h5>
        </div>
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          position: 'relative'
        }}>
          {/* Simulated website background */}
          <div style={{
            position: 'absolute',
            inset: '20px',
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid #e1e4e8'
          }}>
            {/* Fake website header */}
            <div style={{
              height: '40px',
              borderBottom: '1px solid #e1e4e8',
              display: 'flex',
              alignItems: 'center',
              padding: '0 16px',
              gap: '8px'
            }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#ff5f57' }} />
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#febc2e' }} />
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#28c840' }} />
              <div style={{
                flex: 1,
                marginLeft: '12px',
                height: '24px',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px'
              }} />
            </div>
            {/* Fake content lines */}
            <div style={{ padding: '20px' }}>
              <div style={{ height: '12px', backgroundColor: '#f0f0f0', borderRadius: '4px', marginBottom: '12px', width: '80%' }} />
              <div style={{ height: '12px', backgroundColor: '#f0f0f0', borderRadius: '4px', marginBottom: '12px', width: '60%' }} />
              <div style={{ height: '12px', backgroundColor: '#f0f0f0', borderRadius: '4px', marginBottom: '12px', width: '70%' }} />
            </div>
          </div>


          {/* Chat window preview - positioned bottom right */}
          <div
            style={{
              position: 'absolute',
              bottom: '30px',
              right: '30px',
              width: `${windowWidth}px`,
              height: `calc(${windowHeight}% - 60px)`,
              maxHeight: '500px',
              borderRadius: `${windowBorderRadius}px`,
              background: getGradientStyle(),
              boxShadow: '0px -4px 8px rgba(0, 0, 0, 0.2), 0px 8px 16px rgba(0, 0, 0, 0.2)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* Chat Header */}
            <div style={{
              padding: '12px 16px',
              backgroundColor: headerBgColor,
              borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#5C7CFA',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '14px'
                }}>
                  {botTitleName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: headerTextColor }}>{botTitleName}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon icon="more" iconSize={14} color={headerTextColor} style={{ transform: 'rotate(90deg)' }} />
                <Icon icon="cross" iconSize={14} color={headerTextColor} />
              </div>
            </div>


            {/* Messages Area */}
            <div style={{
              flex: 1,
              padding: '16px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {/* Bot Message */}
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', gap: '8px' }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#5C7CFA',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '11px',
                  flexShrink: 0
                }}>
                  B
                </div>
                <div
                  style={{
                    maxWidth: '70%',
                    padding: '0.5rem 0.75rem',
                    borderRadius: hasAdvancedFeatures ? `${botBorderRadius}px` : '8px',
                    border: hasAdvancedFeatures ? `1px solid ${botBorderColor}` : '1px solid #F1F1F1',
                    backgroundColor: hasAdvancedFeatures ? botBgColor : '#FFFFFF',
                    fontSize: '14px',
                    color: hasAdvancedFeatures ? botTextColor : '#000000',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  {initialWelcomeMessage}
                </div>
              </div>


              {/* User Message */}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div
                  style={{
                    maxWidth: '70%',
                    padding: '0.5rem 0.75rem',
                    borderRadius: hasAdvancedFeatures ? `${userBorderRadius}px` : '8px',
                    border: hasAdvancedFeatures ? `1px solid ${userBorderColor}` : '1px solid #F1F1F1',
                    backgroundColor: hasAdvancedFeatures ? userBgColor : '#000000',
                    fontSize: '14px',
                    color: hasAdvancedFeatures ? userTextColor : '#FFFFFF',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  I need help with my account
                </div>
              </div>


              {/* Bot Message */}
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', gap: '8px' }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#5C7CFA',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '11px',
                  flexShrink: 0
                }}>
                  {botTitleName.charAt(0).toUpperCase()}
                </div>
                <div
                  style={{
                    maxWidth: '70%',
                    padding: '0.5rem 0.75rem',
                    borderRadius: hasAdvancedFeatures ? `${botBorderRadius}px` : '8px',
                    border: hasAdvancedFeatures ? `1px solid ${botBorderColor}` : '1px solid #F1F1F1',
                    backgroundColor: hasAdvancedFeatures ? botBgColor : '#FFFFFF',
                    fontSize: '14px',
                    color: hasAdvancedFeatures ? botTextColor : '#000000',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  I'd be happy to help! What seems to be the issue?
                </div>
              </div>
            </div>


            {/* Composer/Input Area - Matching actual webchat */}
            <div style={{
              borderTop: '1px solid #aaaaaa',
              background: '#ffffff',
              paddingBottom: '0.5rem'
            }}>
              <div style={{
                display: 'flex',
                padding: '5px'
              }}>
                {/* Textarea container */}
                <div style={{
                  flexGrow: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {/* xMati Logo Icon - Conditional */}
                  {(hasAdvancedFeatures ? showComposerIcon : true) && (
                    <div style={{
                      width: '38px',
                      height: '38px',
                      marginLeft: '10px',
                      marginRight: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <img
                        src={logo}
                        alt="xMati Logo"
                        style={{ width: '28px', height: '28px', objectFit: 'contain' }}
                      />
                    </div>
                  )}


                  {/* Textarea - with custom styles */}
                  <div style={{
                    width: `${textareaWidth}%`,
                    backgroundColor: textareaBgColor,
                    border: `1px solid ${textareaBorderColor}`,
                    borderRadius: `${textareaBorderRadius}px`,
                    fontSize: '13px',
                    padding: '0.75rem 1rem',
                    color: textareaTextColor,
                    lineHeight: '1.2',
                    minHeight: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    opacity: 0.7
                  }}>
                    Reply to {botTitleName}
                  </div>
                </div>


                {/* Send button - with custom styles */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'stretch',
                  marginLeft: '8px'
                }}>
                  <button style={{
                    border: 'none',
                    borderRadius: `${sendButtonBorderRadius}px`,
                    backgroundColor: sendButtonBgColor,
                    color: sendButtonTextColor,
                    padding: '5px 10px',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    width: `${sendButtonWidth}px`,
                    minHeight: '50px'
                  }}>
                    Send
                  </button>
                </div>
              </div>
            </div>


            {/* Branding Footer - Conditional - Below composer */}
            {(hasAdvancedFeatures ? showBranding : true) && (
              <div style={{
                padding: '8px 12px',
                borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                background: getGradientStyle(),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                fontSize: '11px',
                color: '#000000'
              }}>
                <span>We're by </span>
                <span style={{ fontWeight: 600, marginLeft: '4px', color: '#5C7CFA' }}>xMati</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}


export default ChatInterfaceCustomization





